'use client';

import './stepper-tabbed.scss';

import * as Tabs from '@radix-ui/react-tabs';
import { parseAsInteger, useQueryState } from 'nuqs';
import { useTranslation } from 'react-i18next';

import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.js';
import { FlexibleContentProps } from '@sola/schema/lib/schema.js';
import { trackClick } from '@sola/tracking/components/datalayer.js';

import { SegmentedStepper } from '../../atoms/segmented-stepper/segmented-stepper.tsx';
import { cn } from '../../utils/helpers.ts';
import { StepperCompact } from '../stepper-compact/stepper-compact.tsx';

export type StepperTabbedProps = {
  title?: string;
  items?: Array<FlexibleContentProps>;
};

export const StepperTabbed: React.FC<StepperTabbedProps> = ({
  title,
  items,
}) => {
  const [activeIndex, setActiveIndex] = useQueryState(
    'step',
    parseAsInteger.withDefault(0),
  );
  const { t } = useTranslation('help-me');

  return (
    items &&
    items.length > 0 && (
      <Tabs.Root
        className="StepperTabbed"
        defaultValue="0"
        value={activeIndex.toString()}
        onValueChange={(e) => {
          setActiveIndex(Number(e));
        }}
      >
        <div className="md:hidden">
          <StepperCompact items={items} />
        </div>
        <Tabs.List
          className="StepperTabbed__list hidden md:flex justify-between mx-auto"
          aria-label={title || t('skippy:StepperTabbed.defaultAriaLabel')}
        >
          {items.map((item, index) => (
            <Tabs.Trigger
              className="flex flex-1 justify-center align-center mx-auto gap-snug-xl px-comfy-md py-comfy-xs label-potent label-lg"
              value={index.toString()}
              key={item.title}
              onClick={() => {
                // todo: the user should be able to click forward, but not back
                setActiveIndex(index);
                trackClick(
                  `${index + 1}. ${item.title}`,
                  undefined,
                  'Stepper__desktop',
                );
              }}
            >
              <span className="hidden md:flex">
                {activeIndex > index && (
                  <span className="flex align-center px-snug-xl">
                    <ArkIcon
                      iconName="check-circle"
                      className="StepperTabbed__icon"
                    />
                  </span>
                )}
                <span
                  className={cn('label-potent label-lg', {
                    'StepperTabbed__button--active': activeIndex >= index,
                  })}
                >
                  {activeIndex <= index
                    ? `${index + 1}. ${item.title}`
                    : item.title}
                </span>
              </span>
            </Tabs.Trigger>
          ))}
        </Tabs.List>
        <SegmentedStepper
          count={items.length}
          activeItem={activeIndex}
          className="StepperTabbed__stepper"
        />
        <div className="StepperTabbed__divider" />

        {items.map((item, index) => (
          <Tabs.Content
            className="StepperTabbed__content pt-comfy-lg @lg:pt-comfy-xl align-center justify-around m-auto"
            value={index.toString()}
            key={item.title}
          >
            {item.body}
          </Tabs.Content>
        ))}
      </Tabs.Root>
    )
  );
};
